import { h, clear } from './dom.js';
import { putSample } from '../core/sample-store.js';

// Multi-lane arrangement view. Top lane holds pattern clips; below it are
// stacked audio tracks. Each row has the same horizontal bar grid so the
// playhead lines up across rows.
//
// Pattern clips: click empty area → add active pattern, drag body → move,
// drag right edge → resize, right-click → delete, shift-click → cycle pattern.
//
// Audio tracks: drop a WAV/MP3 onto a row → create an audio clip at that bar
// in that track. Drag clip body → move, right-click → delete.

const PIXELS_PER_BAR = 56;
const ROW_HEIGHT = 56;
const LABEL_WIDTH = 96;

export function mountArrangementView(ctx, slot) {
    clear(slot);

    const header = h('div.lane-header');
    const grid = h('div.arrangement-grid');
    const playhead = h('div.timeline-playhead');
    grid.appendChild(playhead);
    slot.appendChild(header);
    slot.appendChild(grid);

    function modeBtn(mode, label) {
        const cur = ctx.song.getMode() === mode;
        return h('button.btn.mini.mode-btn' + (cur ? '.on' : ''), {
            onClick: () => ctx.song.setMode(mode),
        }, label);
    }

    function renderHeader() {
        clear(header);
        header.appendChild(h('h2', {}, 'Arrangement'));
        header.appendChild(h('div.lane-controls', {}, [
            h('span.hint', {}, 'pattern row: click to drop · drag → move · right-edge → resize · right-click → delete · shift-click → cycle'),
            h('div.mode-toggle', {}, [
                modeBtn('pattern', 'Pattern'),
                modeBtn('song', 'Song'),
            ]),
            h('button.btn', {
                onClick: () => {
                    ctx.song.addAudioTrack();
                    // Create matching mixer channel so the track has volume/EQ/sends/inserts.
                    const tracks = ctx.song.getAudioTracks();
                    const last = tracks[tracks.length - 1];
                    if (last && !ctx.mixer.get(last.channelId)) {
                        ctx.mixer.addChannel({ id: last.channelId, name: last.name, color: '#5fa8d3' });
                    }
                },
            }, '+ Audio Track'),
            h('button.btn.btn-danger', {
                onClick: () => {
                    if (!confirm('Clear the arrangement (pattern clips + audio tracks)?')) return;
                    ctx.song.clear();
                    // Dispose the audio tracks too — we don't auto-remove the
                    // mixer channels because they may still hold useful settings.
                    for (const t of ctx.song.getAudioTracks()) {
                        for (const c of t.clips) ctx.audioClips.removeClip(c.id);
                    }
                    ctx.song.clearAudioTracks();
                },
            }, 'Clear'),
        ]));
    }

    function renderGrid() {
        const total = Math.max(8, ctx.song.totalBarsIncludingAudio(ctx.scheduler.getBPM()) + 4);
        const timelineWidth = total * PIXELS_PER_BAR;

        clear(grid);
        grid.appendChild(makePatternRow(timelineWidth, total));
        for (const track of ctx.song.getAudioTracks()) {
            grid.appendChild(makeAudioRow(track, timelineWidth, total));
        }
        // Append the playhead last so it stacks on top of all rows / clips.
        playhead.style.height = '100%';
        playhead.style.left = `${ctx.song.getCursorBar() * PIXELS_PER_BAR + LABEL_WIDTH}px`;
        grid.appendChild(playhead);
    }

    function makeBarGrid(total) {
        const gridEl = h('div.timeline-grid');
        for (let b = 0; b <= total; b++) {
            gridEl.appendChild(h('div.bar-line' + (b % 4 === 0 ? '.major' : ''), {
                style: { left: `${b * PIXELS_PER_BAR}px` },
            }));
            if (b % 4 === 0) {
                gridEl.appendChild(h('div.bar-label', {
                    style: { left: `${b * PIXELS_PER_BAR + 4}px` },
                }, String(b + 1)));
            }
        }
        return gridEl;
    }

    function makePatternRow(timelineWidth, total) {
        const label = h('div.arrangement-row-label', {}, 'Pattern');
        const timeline = h('div.timeline', {
            style: { width: `${timelineWidth}px`, height: `${ROW_HEIGHT}px` },
        });
        timeline.appendChild(makeBarGrid(total));
        const clipLayer = h('div.timeline-clips');
        for (const clip of ctx.song.getClips()) clipLayer.appendChild(makePatternClipEl(clip));
        timeline.appendChild(clipLayer);

        timeline.addEventListener('click', (e) => {
            if (e.target.closest('.timeline-clip')) return;
            const rect = timeline.getBoundingClientRect();
            const bar = Math.max(0, Math.floor((e.clientX - rect.left) / PIXELS_PER_BAR));
            const activeId = ctx.patterns.getActiveId();
            const pattern = ctx.patterns.getActive();
            if (!activeId || !pattern) return;
            // Native length = pattern.length / 16, allowing fractional clip
            // sizes so an 8-step pattern is exactly half a bar.
            const lengthBars = pattern.length / 16;
            const clip = ctx.song.addClip({ patternId: activeId, startBar: bar, lengthBars });
            if (clip) ctx.bus.emit('history:checkpoint');
        });

        return h('div.arrangement-row', {}, [label, timeline]);
    }

    function makePatternClipEl(clip) {
        const pattern = ctx.patterns.list().find(p => p.id === clip.patternId);
        const name = pattern?.name || '?';
        const pixelW = clip.lengthBars * PIXELS_PER_BAR - 2;
        const isEmpty = pattern && countActiveSteps(pattern) === 0;
        const preview = h('canvas.clip-preview', { width: Math.max(1, Math.floor(pixelW)), height: 28 });
        const el = h('div.timeline-clip' + (isEmpty ? '.empty' : ''), {
            style: {
                left: `${clip.startBar * PIXELS_PER_BAR + 1}px`,
                width: `${pixelW}px`,
            },
            title: isEmpty
                ? `${name} is empty — switch to it in the Patterns bar and click cells in the Channel Rack to add notes`
                : 'Drag to move · drag right edge to resize · right-click to delete · shift-click to cycle pattern',
        }, [
            h('div.clip-label', {}, isEmpty ? `${name} (empty)` : name),
            preview,
            h('div.clip-resize-handle'),
        ]);
        if (pattern) requestAnimationFrame(() => drawPatternPreview(preview, pattern, clip.lengthBars));

        el.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            ctx.song.removeClip(clip.id);
        });

        el.addEventListener('mousedown', (e) => {
            if (e.shiftKey) {
                e.preventDefault();
                cyclePatternClip(clip);
                return;
            }
            const onHandle = e.target.classList.contains('clip-resize-handle');
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startBar = clip.startBar;
            const startLen = clip.lengthBars;
            // Resize snaps to multiples of the pattern's native length so an
            // 8-step pattern resizes in 0.5-bar increments and a 32-step in
            // 2-bar increments — clean loops, never partial repeats.
            const native = pattern ? pattern.length / 16 : 1;
            const onMove = (e2) => {
                const dBarsFloat = (e2.clientX - startX) / PIXELS_PER_BAR;
                if (onHandle) {
                    const target = startLen + dBarsFloat;
                    const snapped = Math.max(native, Math.round(target / native) * native);
                    ctx.song.resizeClip(clip.id, snapped);
                } else {
                    const dBars = Math.round(dBarsFloat);
                    ctx.song.moveClip(clip.id, Math.max(0, startBar + dBars));
                }
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                ctx.bus.emit('history:checkpoint');
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        });
        return el;
    }

    function countActiveSteps(pattern) {
        let n = 0;
        for (const t of Object.values(pattern.tracks || {})) {
            for (const v of (t.steps || [])) if (v) n++;
        }
        return n;
    }

    // Mini-preview of the pattern drawn inside its arrangement clip. Each
    // active step becomes a small dot at the right horizontal position; one
    // row per track so empty vs busy patterns are visible at a glance.
    function drawPatternPreview(canvas, pattern, lengthBars) {
        const ctx2d = canvas.getContext('2d');
        const W = canvas.width;
        const H = canvas.height;
        ctx2d.clearRect(0, 0, W, H);
        const tracks = Object.entries(pattern.tracks || {});
        if (tracks.length === 0) return;
        // Step count we render across: one full pattern at native length,
        // tiled across the clip width if the clip spans more bars.
        const totalSteps = Math.max(1, lengthBars * 16);
        const stepW = W / totalSteps;
        const trackH = H / tracks.length;
        ctx2d.fillStyle = 'rgba(255, 255, 255, 0.85)';
        for (let i = 0; i < tracks.length; i++) {
            const [, track] = tracks[i];
            const steps = track.steps || [];
            const len = steps.length || 1;
            for (let s = 0; s < totalSteps; s++) {
                const v = steps[s % len];
                if (!v) continue;
                const x = s * stepW;
                const y = i * trackH;
                const w = Math.max(1, stepW - 0.5);
                const h = Math.max(1, trackH - 0.5);
                ctx2d.globalAlpha = 0.45 + (typeof v === 'number' ? v : 1) * 0.55;
                ctx2d.fillRect(x, y, w, h);
            }
        }
        ctx2d.globalAlpha = 1;
    }

    function cyclePatternClip(clip) {
        const list = ctx.patterns.list();
        if (list.length === 0) return;
        const idx = list.findIndex(p => p.id === clip.patternId);
        const next = list[(idx + 1) % list.length];
        ctx.song.changeClipPattern(clip.id, next.id);
        ctx.bus.emit('history:checkpoint');
    }

    // ── Audio rows ────────────────────────────────────────────────────
    function makeAudioRow(track, timelineWidth, total) {
        const label = h('div.arrangement-row-label.audio', {}, [
            h('span.audio-row-name', {
                title: 'Double-click to rename',
                ondblclick: () => {
                    const name = prompt('Rename track:', track.name);
                    if (name && name !== track.name) {
                        ctx.song.renameAudioTrack(track.id, name);
                        const ch = ctx.mixer.get(track.channelId);
                        if (ch) ch.setName(name);
                    }
                },
            }, track.name),
            h('button.row-btn.danger', {
                title: 'Remove track',
                onClick: () => {
                    if (!confirm(`Remove "${track.name}" and its clips?`)) return;
                    for (const c of track.clips) ctx.audioClips.removeClip(c.id);
                    ctx.song.removeAudioTrack(track.id);
                },
            }, '✕'),
        ]);

        const timeline = h('div.timeline.audio-track-timeline', {
            style: { width: `${timelineWidth}px`, height: `${ROW_HEIGHT}px` },
        });
        timeline.appendChild(makeBarGrid(total));
        const clipLayer = h('div.timeline-clips');
        const bpm = ctx.scheduler.getBPM();
        for (const clip of track.clips) clipLayer.appendChild(makeAudioClipEl(track, clip, bpm));
        timeline.appendChild(clipLayer);

        // Drag-and-drop a WAV onto the timeline → new audio clip at that bar.
        timeline.addEventListener('dragover', (e) => {
            e.preventDefault();
            timeline.classList.add('drag-over');
        });
        timeline.addEventListener('dragleave', () => timeline.classList.remove('drag-over'));
        timeline.addEventListener('drop', async (e) => {
            e.preventDefault();
            timeline.classList.remove('drag-over');
            const file = e.dataTransfer?.files?.[0];
            if (!file) return;
            const rect = timeline.getBoundingClientRect();
            const bar = Math.max(0, Math.floor((e.clientX - rect.left) / PIXELS_PER_BAR));
            await dropAudio(track, file, bar);
        });

        return h('div.arrangement-row.audio-row', {}, [label, timeline]);
    }

    async function dropAudio(track, file, startBar) {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const audioBuffer = await ctx.Tone.context.decodeAudioData(arrayBuffer.slice(0));
            const clipId = `ac_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;
            const bufferKey = `clip:${clipId}`;
            try { await putSample(bufferKey, file, file.name); } catch (e) { console.warn('persist fail:', e); }
            await ctx.audioClips.addClip(clipId, audioBuffer, track.channelId);
            ctx.song.addAudioClip(track.id, {
                id: clipId, bufferKey, startBar,
                durationSec: audioBuffer.duration, name: file.name,
            });
            ctx.bus.emit('history:checkpoint');
        } catch (err) {
            alert(`Couldn't load ${file.name}: ${err.message}`);
        }
    }

    function makeAudioClipEl(track, clip, bpm) {
        const playSec = Number.isFinite(clip.playSec) ? clip.playSec : (clip.durationSec || 1);
        const offsetSec = clip.offsetSec || 0;
        const fadeInSec = clip.fadeInSec || 0;
        const fadeOutSec = clip.fadeOutSec || 0;
        const lengthBars = Math.max(0.05, playSec * bpm / 240);
        const pixelW = Math.max(20, lengthBars * PIXELS_PER_BAR - 2);
        const pxPerSec = (PIXELS_PER_BAR * bpm) / 240;
        const fadeInPx = Math.min(pixelW, fadeInSec * pxPerSec);
        const fadeOutPx = Math.min(pixelW, fadeOutSec * pxPerSec);

        const waveform = h('canvas.audio-waveform', {
            width: Math.max(1, Math.floor(pixelW)), height: 36,
        });
        const fadeInOverlay = h('div.fade-overlay.in', { style: { width: `${fadeInPx}px` } });
        const fadeOutOverlay = h('div.fade-overlay.out', { style: { width: `${fadeOutPx}px` } });
        const fadeInHandle = h('div.fade-handle.in', { style: { left: `${fadeInPx}px` }, title: 'Drag → for fade in' });
        const fadeOutHandle = h('div.fade-handle.out', { style: { right: `${fadeOutPx}px` }, title: 'Drag ← for fade out' });
        const trimLeft = h('div.audio-trim-handle.left', { title: 'Drag to trim start' });
        const trimRight = h('div.audio-trim-handle.right', { title: 'Drag to trim end' });

        const el = h('div.timeline-clip.audio-clip', {
            style: {
                left: `${clip.startBar * PIXELS_PER_BAR + 1}px`,
                width: `${pixelW}px`,
            },
            title: 'Drag body to move · edge handles trim · top-corner handles fade · right-click to remove',
        }, [
            waveform,
            fadeInOverlay,
            fadeOutOverlay,
            h('div.clip-label', {}, clip.name || 'audio'),
            trimLeft, trimRight,
            fadeInHandle, fadeOutHandle,
        ]);

        // Render waveform once layout settles.
        requestAnimationFrame(() => drawWaveform(waveform, clip));

        el.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            ctx.audioClips.removeClip(clip.id);
            ctx.song.removeAudioClip(track.id, clip.id);
        });

        // Body drag → move clip on the timeline (whole-bar snap).
        el.addEventListener('mousedown', (e) => {
            // Skip if a handle was the actual target — those have their own
            // mousedown listeners and we don't want both to fire.
            if (e.target.classList.contains('audio-trim-handle')) return;
            if (e.target.classList.contains('fade-handle')) return;
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startBar = clip.startBar;
            const onMove = (e2) => {
                const dBars = Math.round((e2.clientX - startX) / PIXELS_PER_BAR);
                ctx.song.moveAudioClip(track.id, clip.id, Math.max(0, startBar + dBars));
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                ctx.bus.emit('history:checkpoint');
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        });

        // Left trim → moves startBar AND offsetSec so the visual right edge
        // stays put. Snaps to 1/16-bar steps for fine control.
        trimLeft.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startBarStart = clip.startBar;
            const startOffset = offsetSec;
            const startPlay = playSec;
            const stepBars = 1 / 16;
            const onMove = (e2) => {
                const dBars = Math.round(((e2.clientX - startX) / PIXELS_PER_BAR) / stepBars) * stepBars;
                const dSec = dBars * 240 / bpm;
                let newOffset = Math.max(0, startOffset + dSec);
                let newPlay = startPlay - dSec;
                if (newPlay < 0.05) {
                    newPlay = 0.05;
                    newOffset = startOffset + (startPlay - 0.05);
                }
                if (newOffset > clip.durationSec - 0.05) {
                    newOffset = clip.durationSec - 0.05;
                    newPlay = startPlay - (newOffset - startOffset);
                }
                const newStartBar = Math.max(0, startBarStart + (newOffset - startOffset) * bpm / 240);
                ctx.song.updateAudioClip(track.id, clip.id, {
                    startBar: newStartBar,
                    offsetSec: newOffset,
                    playSec: newPlay,
                });
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                ctx.bus.emit('history:checkpoint');
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        });

        // Right trim → adjust playSec only; visual right edge moves.
        trimRight.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startPlay = playSec;
            const onMove = (e2) => {
                const dBars = (e2.clientX - startX) / PIXELS_PER_BAR;
                const dSec = dBars * 240 / bpm;
                const maxPlay = Math.max(0.05, (clip.durationSec || 1) - offsetSec);
                const newPlay = Math.max(0.05, Math.min(maxPlay, startPlay + dSec));
                ctx.song.updateAudioClip(track.id, clip.id, { playSec: newPlay });
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                ctx.bus.emit('history:checkpoint');
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        });

        // Fade-in handle: drag right inside the clip → longer fade.
        fadeInHandle.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startFade = fadeInSec;
            const onMove = (e2) => {
                const dPx = e2.clientX - startX;
                const newFade = Math.max(0, Math.min(playSec / 2, startFade + dPx / pxPerSec));
                ctx.song.updateAudioClip(track.id, clip.id, { fadeInSec: newFade });
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                ctx.bus.emit('history:checkpoint');
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        });

        // Fade-out handle: drag left inside the clip → longer fade out.
        fadeOutHandle.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startFade = fadeOutSec;
            const onMove = (e2) => {
                const dPx = startX - e2.clientX; // dragging left = longer fade
                const newFade = Math.max(0, Math.min(playSec / 2, startFade + dPx / pxPerSec));
                ctx.song.updateAudioClip(track.id, clip.id, { fadeOutSec: newFade });
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                ctx.bus.emit('history:checkpoint');
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        });

        return el;
    }

    // Draw the audio clip's waveform — min/max peaks per pixel column with
    // the trimmed-out portions dimmed so the user sees what offsetSec /
    // playSec exclude.
    function drawWaveform(canvas, clip) {
        const W = canvas.width;
        const H = canvas.height;
        const ctx2d = canvas.getContext('2d');
        ctx2d.clearRect(0, 0, W, H);
        if (!clip.durationSec || clip.durationSec <= 0) return;
        const peaks = ctx.audioClips.getPeaks(clip.id, Math.max(8, Math.floor(W / 2)));
        if (!peaks) return;
        const buckets = peaks.length / 2;
        const colW = W / buckets;
        ctx2d.fillStyle = 'rgba(255, 255, 255, 0.85)';
        for (let i = 0; i < buckets; i++) {
            const min = peaks[i * 2];
            const max = peaks[i * 2 + 1];
            const yMax = H / 2 - max * (H / 2);
            const yMin = H / 2 - min * (H / 2);
            ctx2d.fillRect(i * colW, yMax, Math.max(1, colW - 0.4), Math.max(1, yMin - yMax));
        }
    }

    function updatePlayhead(fractionalBar) {
        playhead.style.left = `${fractionalBar * PIXELS_PER_BAR + LABEL_WIDTH}px`;
    }

    function render() { renderHeader(); renderGrid(); }
    render();

    ctx.bus.on('song:clipsChanged', render);
    ctx.bus.on('song:modeChanged', renderHeader);
    ctx.bus.on('audio:tracksChanged', render);
    ctx.bus.on('audio:tracksRestored', render);
    ctx.bus.on('pattern:listChanged', render);
    ctx.bus.on('project:loaded', render);
    ctx.bus.on('arrangement:position', updatePlayhead);
    ctx.bus.on('song:cursorChanged', (bar) => updatePlayhead(bar));
    ctx.bus.on('transport:stopped', () => updatePlayhead(0));
    // Re-render on BPM changes (audio clip widths depend on tempo) and
    // whenever a pattern's contents change so the mini-preview stays accurate.
    ctx.bus.on('transport:bpmChanged', renderGrid);
    ctx.bus.on('history:checkpoint', renderGrid);
    ctx.bus.on('pattern:cleared', renderGrid);
    ctx.bus.on('pattern:lengthChanged', renderGrid);
}
